// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import type { FundSearchResult } from '@shared/portfolio-operations/types';
import type { AssetsSearchResp } from '@shared/data-access/interfaces/api/ai/assets';

export interface UseFundSearchInputProps {
  onSelectFund: (fund: FundSearchResult) => void;
  excludedTickers: string[];
  portfolioFunds?: FundSearchResult[];
}

export const useFundSearchInput = ({
  onSelectFund,
  excludedTickers,
  portfolioFunds,
}: UseFundSearchInputProps) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');

  const onSelectValue = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  const onSelectDetails = useCallback(
    (fund: AssetsSearchResp | undefined) => {
      if (fund && !excludedTickers.includes(fund.ticker)) {
        const fundResult: FundSearchResult = {
          ticker: fund.ticker,
          name: fund.name,
          asset_type: fund.asset_type,
          url_logo: fund.url_logo,
          url_morningstar: fund.url_morningstar,
          isin: fund.isin,
          dfi_url: fund.dfi_url,
        };
        onSelectFund(fundResult);
        setSearchValue('');
      }
    },
    [excludedTickers, onSelectFund],
  );

  const filteredPortfolioFunds = portfolioFunds?.filter(
    fund => !excludedTickers.includes(fund.ticker),
  );

  return {
    t,
    searchValue,
    onSelectValue,
    onSelectDetails,
    filteredPortfolioFunds,
    excludedTickers,
  };
};
